/**
 * 学员端 - 我的纠错页面
 *
 * 功能:
 * 1. 展示学员提交的所有纠错记录列表
 * 2. 按状态筛选: 全部 / 待审核 / 已通过 / 已拒绝
 * 3. 每条记录显示: 纠错类型、错误描述、提交时间、审核状态、奖励U币
 * 4. 点击【查看详情】弹出模态框展示完整信息
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractBeijingTime } from '../utils/timeUtils';
import { supabase } from '../supabase/client';
import { Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, Filter, Eye } from 'lucide-react';
import CorrectionDetailModal from '../components/CorrectionDetailModal';

interface Correction {
  id: string;
  submitter_id: string;
  submitter_username?: string;
  target_type: string;
  target_id: string;
  target_detail: any;
  error_description: string;
  suggested_correction: string | null;
  evidence_images: string[] | null;
  severity: string | null;
  status: string;
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: string | null;
  reward_points: number | null;
  rewarded_at: string | null;
  created_at: string;
  updated_at: string;

  // 扩展字段（用于显示纠错对象）
  paper_title?: string | null;
  question_title?: string | null;
  question_type?: string | null;
  question_order_index?: number | null;

  // 题目详细信息（用于详情弹窗展示，对齐 CorrectionDetailModal）
  question_content?: string | null;
  question_options?: any | null;
  reference_answer?: string | null;
  explanation?: string | null;
  scoring_points?: any | null;
  title_images?: string[] | null;
  title_image_widths?: number[] | null;
  title_image_layout?: string | null;
  image_position?: string | null;
  option_layout?: string | null;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  question: '题目纠错',
  paper: '试卷纠错',
  answer: '答案纠错',
  option: '选项纠错',
  explanation: '解析纠错',
  other: '其他问题',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: '待审核', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  reviewing: { label: '审核中', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  approved: { label: '已通过', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: '已拒绝', color: 'bg-red-100 text-red-700', icon: XCircle },
  rewarded: { label: '已奖励', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  processed: { label: '已处理', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
};


const MyCorrections: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedCorrection, setSelectedCorrection] = useState<Correction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchCorrections();
    }
  }, [userId, filterStatus]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUserId(user.id);
    } else {
      setLoading(false);
    }
  };

  const fetchCorrections = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('error_corrections')
        .select('*')
        .eq('submitter_id', userId!)
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch corrections:', error);
        setCorrections([]);
      } else if (data && data.length > 0) {
        // 获取题目信息（用于显示纠错对象）
        const questionIds = [...new Set(data.filter(c => c.target_type === 'question' && c.target_id).map(c => c.target_id))];
        let questionMap = new Map();
        let paperMap = new Map();

        if (questionIds.length > 0) {
          const { data: questions } = await supabase
            .from('questions')
            .select('id, title, question_type, content, paper_id, order_index, reference_answer, explanation, scoring_points, options, title_images, title_image_widths, title_image_layout, image_position, option_layout')
            .in('id', questionIds);

          questionMap = new Map((questions || []).map(q => [q.id, q]));

          // 通过 paper_id 关联查询试卷名称
          const paperIds = [...new Set((questions || []).filter(q => q.paper_id).map(q => q.paper_id))];
          if (paperIds.length > 0) {
            const { data: papers } = await supabase
              .from('papers')
              .select('id, title')
              .in('id', paperIds);

            paperMap = new Map((papers || []).map(p => [p.id, p.title]));
          }
        }

        const correctionsWithInfo = data.map(c => {
          const question = c.target_type === 'question' ? questionMap.get(c.target_id) : null;
          // 优先使用从 papers 表查询的真实试卷名称
          const realPaperTitle = question?.paper_id ? paperMap.get(question.paper_id) : null;
          return {
            ...c,
            submitter_username: '我', // 学员端显示为"我"
            paper_title: realPaperTitle || c.paper_title || '未命名试卷',
            question_title: question?.title || null,
            question_type: question?.question_type || null,
            question_order_index: question?.order_index || null,
            question_content: question?.content || null,
            question_options: question?.options || null,
            reference_answer: question?.reference_answer || null,
            explanation: question?.explanation || null,
            scoring_points: question?.scoring_points || null,
            title_images: question?.title_images || null,
            title_image_widths: question?.title_image_widths || null,
            title_image_layout: question?.title_image_layout || null,
            image_position: question?.image_position || null,
            option_layout: question?.option_layout || null,
          };
        });

        setCorrections(correctionsWithInfo);
      } else {
        setCorrections([]);
      }
    } catch (err) {
      console.error('Failed to fetch corrections:', err);
      setCorrections([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  const openDetailModal = (correction: Correction) => {
    setSelectedCorrection(correction);
    setShowDetailModal(true);
  };

  const statusCounts = {
    all: corrections.length,
    pending: corrections.filter(c => c.status === 'pending').length,
    approved: corrections.filter(c => c.status === 'approved' || c.status === 'rewarded').length,
    rejected: corrections.filter(c => c.status === 'rejected').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">我的纠错</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 统计概览 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-100">
            <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
            <div className="text-xs text-yellow-500 mt-1">待审核</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
            <div className="text-2xl font-bold text-green-600">{statusCounts.approved}</div>
            <div className="text-xs text-green-500 mt-1">已通过</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
            <div className="text-2xl font-bold text-red-600">{statusCounts.rejected}</div>
            <div className="text-xs text-red-500 mt-1">已拒绝</div>
          </div>
        </div>

        {/* 状态筛选 */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-4 h-4 text-gray-400" />
            {[
              { key: 'all', label: '全部' },
              { key: 'pending', label: '待审核' },
              { key: 'approved', label: '已通过' },
              { key: 'rejected', label: '已拒绝' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filterStatus === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
                {key !== 'all' && statusCounts[key as keyof typeof statusCounts] > 0 && (
                  <span className="ml-1 text-xs opacity-75">
                    ({statusCounts[key as keyof typeof statusCounts]})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 纠错记录列表 */}
        {corrections.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✏️</div>
            <div className="text-gray-500 mb-2">暂无纠错记录</div>
            <div className="text-sm text-gray-400">答题时发现错误可以提交纠错，审核通过后可获得U币奖励</div>
          </div>
        ) : (
          <div className="space-y-3">
            {corrections.map((correction) => {
              const statusInfo = STATUS_CONFIG[correction.status] || { label: correction.status, color: 'bg-gray-100 text-gray-700', icon: Clock };
              return (
                <div key={correction.id} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4 overflow-x-auto pb-2 sm:pb-0">
                    {/* 1. 纠错类型 */}
                    <div className="flex-shrink-0">
                      <span className="text-sm font-medium text-gray-800 whitespace-nowrap">
                        {TARGET_TYPE_LABELS[correction.target_type] || correction.target_type}
                      </span>
                    </div>

                    {/* 分隔符 */}
                    <div className="w-px h-4 bg-gray-200 flex-shrink-0"></div>

                    {/* 2. 提交时间 */}
                    <div className="flex items-center gap-1 flex-shrink-0 text-xs text-gray-500 whitespace-nowrap">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(correction.created_at)}</span>
                    </div>

                    {/* 分隔符 */}
                    <div className="w-px h-4 bg-gray-200 flex-shrink-0"></div>

                    {/* 3. 审核状态 */}
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusInfo.color}`}>
                        {React.createElement(statusInfo.icon, { className: 'w-3 h-3' })}
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* 分隔符 */}
                    <div className="w-px h-4 bg-gray-200 flex-shrink-0"></div>

                    {/* 4. U币奖励 */}
                    <div className="flex-shrink-0 text-right">
                      {correction.reward_points && correction.reward_points > 0 ? (
                        <span className="text-sm font-bold text-orange-600 whitespace-nowrap">+{correction.reward_points}U币</span>
                      ) : (
                        <span className="text-sm text-gray-400 whitespace-nowrap">无U币</span>
                      )}
                    </div>

                    {/* 分隔符 */}
                    <div className="w-px h-4 bg-gray-200 flex-shrink-0"></div>

                    {/* 5. 审核时间 */}
                    <div className="flex items-center gap-1 flex-shrink-0 text-xs text-gray-500 whitespace-nowrap">
                      {correction.reviewed_at ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span>{formatDate(correction.reviewed_at)}</span>
                        </>
                      ) : (
                        <span className="text-gray-400">未审核</span>
                      )}
                    </div>

                    {/* 弹性空间 */}
                    <div className="flex-1"></div>

                    {/* 6. 查看详情按钮 */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => openDetailModal(correction)}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm whitespace-nowrap"
                      >
                        <Eye className="w-4 h-4" />
                        <span>查看详情</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 纠错详情弹窗 - 使用公共组件 CorrectionDetailModal */}
      {showDetailModal && selectedCorrection && (
        <CorrectionDetailModal
          correction={selectedCorrection}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          isAdminMode={false}
        />
      )}
    </div>
  );
};

export default MyCorrections;
